import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CreditApplicationDTO, fetchApplicationById } from '../../services/creditService';
import '../../styles/AccountTools.css';

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : 'Not available');

const CreditApplicationDetails = () => {
  const { applicationId } = useParams();
  const [application, setApplication] = useState<CreditApplicationDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadApplication = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      setApplication(await fetchApplicationById(applicationId));
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Failed to load application.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  return (
    <main className="tool-page">
      <div className="tool-header">
        <div>
          <h1>Credit Application Details</h1>
          <p>ID: {applicationId}</p>
        </div>
        <Link className="tool-btn-secondary" to="/account/credits/applications">Back to Applications</Link>
      </div>

      {message && <div className="tool-message error">{message}</div>}
      {loading || !application ? (
        <div className="tool-loading">Loading application...</div>
      ) : (
        <div className="tool-grid">
          <section className="tool-card">
            <h2>Application</h2>
            <Info label="Status" value={application.status || 'Not available'} />
            <Info label="Description" value={application.description || 'Not provided'} />
            <Info label="Submitted" value={formatDate(application.createdAt)} />
            <Info label="Last update" value={formatDate(application.updatedAt)} />
            <Info label="Decision date" value={formatDate(application.approvalDenialDate)} />
          </section>

          <section className="tool-card">
            <h2>Purposes</h2>
            <div className="tool-chips">
              {(application.purposes || []).length ? (
                (application.purposes || []).map((purpose) => <span className="tool-pill" key={purpose.id}>{purpose.name}</span>)
              ) : (
                <span className="tool-pill neutral">Not provided</span>
              )}
            </div>
          </section>

          <section className="tool-card">
            <h2>Contacts and References</h2>
            <Info label="Contact" value={application.contactInfo?.name || 'Not provided'} />
            <Info label="Contact phone" value={application.contactInfo?.phone || 'Not provided'} />
            <Info label="Contact email" value={application.contactInfo?.email || 'Not provided'} />
            {(application.references || []).map((reference, index) => (
              <Info key={`${reference.name}-${index}`} label={`Reference ${index + 1}`} value={`${reference.name} - ${reference.relationship}`} />
            ))}
          </section>

          <section className="tool-card">
            <h2>Attachments</h2>
            <Info label="Passport photo" value={application.passportPhoto ? 'Attached' : 'Not attached'} />
            <Info label="Photos" value={`${application.photos?.length || 0}`} />
            <Info label="Documents" value={`${application.documents?.length || 0}`} />
          </section>

          {!!application.credits?.length && (
            <section className="tool-card">
              <h2>Issued Credits</h2>
              <div className="tool-list">
                {application.credits.map((credit) => (
                  <div className="tool-row" key={credit.id}>
                    <div className="tool-row-main">
                      <div className="tool-row-title">{credit.code ? `#${credit.code}` : 'Credit'}</div>
                      <div className="tool-row-sub">{credit.status || 'Status unavailable'}</div>
                    </div>
                    <span className="tool-pill">{credit.unit === 'TIME' ? `${Number(credit.balance ?? credit.amount ?? 0).toFixed(0)} min` : `${credit.currency || 'USD'} ${Number(credit.balance ?? credit.amount ?? 0).toFixed(2)}`}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </main>
  );
};

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="tool-row">
      <div className="tool-row-title">{label}</div>
      <div className="tool-row-sub">{value}</div>
    </div>
  );
}

export default CreditApplicationDetails;
